import { OnInit, DestroyRef, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ListService } from '@abp/ng.core';
import { CardBodyComponent, CardComponent, ConfirmationService } from '@abp/ng.theme.shared';
import { BaseCrudComponent, CrudPage, IActionsSchema, ICrudService, resolveActions } from '@zendra/shared';
import { PlantationsToGeneratePlanningOrchestratorService } from 'orchestrator/proxy';
import type {
  GeneratePlantationsToGenerateInputDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/plantation/models';
import type {
  GetPlantationsToGenerateInputDto,
  PlantationToGenerateDto,
  PlantationsToGenerateExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/integrations/bio-forest-abp/models';
import { catchError, EMPTY, map, Observable, tap, throwError } from 'rxjs';
import {
  PLANTATIONS_TO_GENERATE_CONFIG,
  PLANTATIONS_TO_GENERATE_FIELDS,
  PLANTATIONS_TO_GENERATE_TABLE_OPTIONS,
} from './plantations-to-generate.config';

type PlantationToGenerateRow = PlantationToGenerateDto & { id: number };

type UnsupportedCrudDto = Record<string, never>;

@Component({
  selector: 'app-plantations-to-generate',
  imports: [CardComponent, CardBodyComponent, CrudPage],
  providers: [ListService, ConfirmationService],
  templateUrl: './plantations-to-generate.html',
})
export class PlantationsToGenerate extends BaseCrudComponent<
  PlantationToGenerateRow,
  UnsupportedCrudDto,
  UnsupportedCrudDto,
  PlantationsToGenerateExcelDownloadDto
> implements OnInit {
  private readonly plantationsToGenerateService = inject(PlantationsToGeneratePlanningOrchestratorService);
  private readonly destroyRef = inject(DestroyRef);

  readonly config = PLANTATIONS_TO_GENERATE_CONFIG;
  readonly fields = PLANTATIONS_TO_GENERATE_FIELDS;
  readonly tableOptions = PLANTATIONS_TO_GENERATE_TABLE_OPTIONS;
  actions: IActionsSchema | null = null;

  ngOnInit(): void {
    this.actions = resolveActions(this.config.actions, {
      [this.config.actionIds.generatePlantationsTrigger]: () => this.generatePlantations(),
    });
  }

  service: ICrudService<
    PlantationToGenerateRow,
    UnsupportedCrudDto,
    UnsupportedCrudDto,
    PlantationsToGenerateExcelDownloadDto
  > = {
    create: (_input: UnsupportedCrudDto) => this.unsupportedCrudOperation('create'),
    update: (_id: number, _input: UnsupportedCrudDto) => this.unsupportedCrudOperation('update'),
    deleteByIds: (_ids: number[]) => this.unsupportedCrudOperation('delete'),
    getList: (query: GetPlantationsToGenerateInputDto) =>
      this.plantationsToGenerateService.getList(query).pipe(
        map(response => ({
          items: this.withSyntheticIds(response.items ?? [], query?.skipCount ?? 0),
          totalCount: response.totalCount ?? 0,
        })),
      ),
    getDownloadToken: () => this.plantationsToGenerateService.getDownloadToken(),
    getListAsExcelFile: input =>
      this.plantationsToGenerateService.getListAsExcelFile(
        input as PlantationsToGenerateExcelDownloadDto,
      ),
  };

  protected prepareUpdateDto(
    _data: any,
    _selected: PlantationToGenerateRow,
  ): UnsupportedCrudDto {
    return {};
  }

  protected getEntityName(): string {
    return 'PlantationsToGenerate';
  }

  private generatePlantations(): void {
    const selectedRows = this.dataset().filter(row => this.selectedRows.includes(row.id));

    if (!selectedRows.length) {
      return;
    }

    const input: GeneratePlantationsToGenerateInputDto = {
      items: selectedRows.map(row => ({
        uiRowId: row.id,
        standId: row.standId,
        documentDate: row.documentDate,
        spacingId: row.spacingId,
        geneticMaterialId: row.geneticMaterialId,
      })),
    };

    this.plantationsToGenerateService
      .generate(input)
      .pipe(
        tap(result => this.bulkActionResultService.handle(result, { selectedCount: selectedRows.length })),
        catchError(error => {
          console.error(error);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private withSyntheticIds(
    items: PlantationToGenerateDto[],
    skipCount: number,
  ): PlantationToGenerateRow[] {
    return items.map((item, index) => ({
      ...item,
      id: skipCount + index + 1,
    }));
  }

  private unsupportedCrudOperation<T>(operation: string): Observable<T> {
    return throwError(
      () =>
        new Error(
          `Plantations to generate is a read-only Bioforest projection, so ${operation} is not supported.`,
        ),
    );
  }
}
